/**
 * L'activité que le starting block a promise, retrouvée par le live.
 *
 * Au bout du démarrage, Sam peut lancer la génération d'une activité (le statut
 * `new_activity_generation` : « Allez, quiz d'échauffement sur … ! »). Le serveur la fabrique en file
 * (`GenerateNewActivityJob`, son quiz de prérequis compris), la rattache à la conversation
 * (`conversations.activity_id`), puis la pousse à la salle (`newTodo`, `forceFocus`). Mais l'élève n'est
 * dans la salle qu'après « C'est parti ! » : une génération finie avant (7 s mesurées en sandbox)
 * poussait dans une salle où il n'était pas encore, et le live ouvrait une autre activité — le quiz
 * promis n'arrivait jamais (retour du weekly du 20/10).
 *
 * Le démarrage retient donc la promesse : la conversation, et les activités à faire qu'il connaissait.
 * Le live, quand la liste relue porte une activité nouvelle, demande au serveur laquelle la
 * conversation a fait générer (`GET …/conversations/{id}/generated-activity`) et n'ouvre que celle-là,
 * par son id — une fois, puis oublie. Jamais « la prochaine qui arrive » : quand la génération a échoué
 * (le serveur ne le dit pas), la prochaine est une activité du tuteur, qui ne s'ouvre pas de force (elle
 * quitterait l'activité en cours, et la finaliserait si elle a une copie). Sans réponse au bout de
 * `GENERATION_WINDOW_MS`, la promesse tombe.
 *
 * Un registre en mémoire, comme `liveScreenPresence` : le démarrage et le live se suivent dans la même
 * session de l'app ; l'app relancée entre les deux reprend l'activité rangée.
 *
 * Quand une activité de la liste répond déjà au besoin, Sam ne génère rien : il la lance
 * (`todo_activity_launch`, `launchedTodoId`), que le live ouvre comme sa suggestion.
 */
import { type ApiCheckpointConversation } from "./checkpointConversationMachine"

/** Une activité à faire, telle que la liste du compte la sert. */
export type GeneratedTodoCandidate = { id?: number | null; finished_at?: string | null }

/** Ce que le serveur sait de la génération : l'activité rattachée, `null` tant qu'il n'y en a pas. */
export type GeneratedActivityAnswer = { activity_id?: number | null } | null

/** Au-delà, la génération n'aboutira plus (son appel au service d'IA plafonne à 180 s). */
export const GENERATION_WINDOW_MS = 10 * 60 * 1000

/** Le statut d'un tour qui lance la génération d'une activité. */
export const ACTIVITY_GENERATION_STATUS = "new_activity_generation"

/** Le statut d'un tour qui lance une activité de la liste (celle qui répond au besoin, ou le plan du masteur). */
const TODO_LAUNCH_STATUS = "todo_activity_launch"

/** L'activité de la liste qu'un tour de Sam lance, ou `null`. */
export function launchedTodoId(turn: ApiCheckpointConversation | null | undefined): number | null {
    const conversation = turn?.conversation
    if (conversation?.status !== TODO_LAUNCH_STATUS) return null
    const id = Number(conversation.key_info?.next_todo_activity_id)
    return Number.isInteger(id) && id > 0 ? id : null
}

type Promised = {
    /** La conversation qui génère ; aucune pour un lancement, dont l'activité est connue. */
    conversationId: number | string | null
    /** Un lancement depuis la liste, pas une génération (la télémétrie les distingue). */
    launched: boolean
    /** Les activités à faire au démarrage : aucune n'est la générée, rien à demander pour elles. */
    known: Set<number>
    at: number
    /** Une question au serveur est en route. */
    asking: boolean
    /** L'activité que le serveur a nommée, pas encore dans la liste relue. */
    activityId: number | null
}

const promised = new Map<string, Promised>()

const idsOf = (todos: GeneratedTodoCandidate[]) =>
    todos.map((todo) => todo.id).filter((id): id is number => typeof id === "number")

const isTodo = (todos: GeneratedTodoCandidate[], id: number) =>
    todos.some((todo) => todo.id === id && !todo.finished_at)

/** Le démarrage vient de promettre une activité pour cette séance, générée par cette conversation. */
export function rememberGeneration(
    liveSessionId: number | string,
    conversationId: number | string,
    knownTodos: GeneratedTodoCandidate[],
    now: number = Date.now()
): void {
    promised.set(String(liveSessionId), {
        conversationId,
        known: new Set(idsOf(knownTodos)),
        at: now,
        asking: false,
        activityId: null,
        launched: false,
    })
}

/**
 * Le démarrage vient de lancer une activité de la liste, connue par son id (celle que le serveur a
 * choisie) : le live l'ouvre dès que la liste relue la porte. Elle a pu être créée pendant le test du
 * matériel, après la dernière lecture de la liste : le live aurait ouvert une autre activité, sans
 * revenir dessus.
 */
export function rememberLaunch(liveSessionId: number | string, activityId: number, now: number = Date.now()): void {
    promised.set(String(liveSessionId), {
        conversationId: null,
        known: new Set(),
        at: now,
        asking: false,
        activityId,
        launched: true,
    })
}

/**
 * Ce que le live fait de la liste relue : ouvrir l'activité générée (`open`, et la promesse s'oublie),
 * demander au serveur laquelle c'est (`ask`, une activité nouvelle est apparue), ou rien (`null`).
 * Promesse trop ancienne : oubliée.
 */
export function generationStep(
    liveSessionId: number | string,
    todos: GeneratedTodoCandidate[],
    now: number = Date.now()
): { open: number; launched: boolean } | { ask: number | string } | null {
    const key = String(liveSessionId)
    const promise = promised.get(key)
    if (promise == null) return null
    if (now - promise.at > GENERATION_WINDOW_MS) {
        promised.delete(key)
        return null
    }
    if (promise.activityId != null && isTodo(todos, promise.activityId)) {
        promised.delete(key)
        return { open: promise.activityId, launched: promise.launched }
    }
    if (promise.asking || promise.activityId != null) return null
    const arrived = todos.some(
        (todo) => typeof todo.id === "number" && !promise.known.has(todo.id) && !todo.finished_at
    )
    if (!arrived || promise.conversationId == null) return null
    promise.asking = true
    return { ask: promise.conversationId }
}

/**
 * La réponse du serveur, lue contre la liste du moment : l'activité générée à ouvrir, ou `null`. Sans
 * activité rattachée (la génération tourne encore, ou a échoué), rien ne s'ouvre : la relecture suivante
 * repose la question. Nommée mais pas encore dans la liste, elle s'ouvrira à la relecture qui la porte.
 */
export function settleGeneration(
    liveSessionId: number | string,
    answer: GeneratedActivityAnswer,
    todos: GeneratedTodoCandidate[]
): number | null {
    const key = String(liveSessionId)
    const promise = promised.get(key)
    if (promise == null) return null
    promise.asking = false
    const id = answer?.activity_id
    if (typeof id === "number") {
        if (isTodo(todos, id)) {
            promised.delete(key)
            return id
        }
        promise.activityId = id
    }
    return null
}

/** La question au serveur a échoué : la prochaine relecture la repose. */
export function generationAskFailed(liveSessionId: number | string): void {
    const promise = promised.get(String(liveSessionId))
    if (promise != null) promise.asking = false
}

/** Les tests repartent d'un registre vide. */
export function forgetAllGenerations(): void {
    promised.clear()
}
